import { Button, Col, Row } from 'antd';
import { useFormikContext } from 'formik';
import { groupBy, has } from 'lodash/fp';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type BankDetailsDataFragment } from '../../../../api/fragments/BankDetailsData';
import { type FinanceProductType } from '../../../../api/types';
import { useCompany } from '../../../../components/contexts/CompanyContextManager';
import SwitchField from '../../../../components/fields/SwitchField';
import TransferField from '../../../../components/fields/TransferField';
import CollapsibleWrapper, { Panel } from '../../../../components/wrappers/CollapsibleWrapper';
import renderMarkdown from '../../../../utilities/renderMarkdown';
import useSystemSwitchData from '../../../../utilities/useSystemSwitchData';
import useTranslatedString from '../../../../utilities/useTranslatedString';
import useStaticFinanceProductType from '../../../shared/staticFinanceProductType';

type RecordType = {
    key: string;
    title: string;
};

const colSpan = { lg: 8, md: 12, xs: 24 };

export type FinanceProductTypeFormProps = {
    bank?: BankDetailsDataFragment;
    isUpdate: boolean;
    disabled?: boolean;
};

const filterOption = (inputValue: string, option: RecordType) =>
    option.title.toLowerCase().indexOf(inputValue.toLowerCase()) > -1;

const FinanceProductTypeForm = ({ bank, isUpdate, disabled = false }: FinanceProductTypeFormProps) => {
    const { t } = useTranslation(['bankDetails', 'common', 'financeProductDetails']);
    const translate = useTranslatedString();
    const company = useCompany(true);
    const { financeProductTypeOptions } = useStaticFinanceProductType(company?.countryCode);

    const { yesNoSwitch } = useSystemSwitchData();

    const financeProducts = useMemo(() => {
        if (isUpdate && bank?.financeProducts.length) {
            return groupBy('type', bank.financeProducts);
        }

        return {};
    }, [bank?.financeProducts, isUpdate]);

    const {
        values: { availableFinanceProductTypes: selectedKeys },
    } = useFormikContext<BankDetailsDataFragment>();

    const availableFinanceProductType = useMemo(() => {
        if (isUpdate) {
            const usedFinanceProductTypes = selectedKeys.filter((selectedFP: FinanceProductType) =>
                has(selectedFP, financeProducts)
            );

            if (usedFinanceProductTypes.length) {
                const disabledUsedFinanceProductTypes = financeProductTypeOptions.map(fpOption => {
                    if (usedFinanceProductTypes.includes(fpOption.key)) {
                        return {
                            ...fpOption,
                            title: `${fpOption.title}*`,
                            disabled: true,
                        };
                    }

                    return fpOption;
                });

                return disabledUsedFinanceProductTypes;
            }

            return financeProductTypeOptions;
        }

        return financeProductTypeOptions;
    }, [financeProductTypeOptions, financeProducts, isUpdate, selectedKeys]);

    const usedFinanceProductTypeCopywriting = useMemo(() => {
        if (isUpdate) {
            const copywritingArr = [];
            Object.keys(financeProducts).forEach(key => {
                const usedFP = key;
                const usedIn = financeProducts[key].map(fp => translate(fp.legalName));
                const financeProductType = t(
                    `financeProductDetails:types.${usedFP.charAt(0).toLowerCase() + usedFP.slice(1)}`
                );
                const financeProductList = usedIn.join(',');
                copywritingArr.push(
                    t('bankDetails:usedFinanceProductType.descriptionNotes', {
                        financeProductType,
                        financeProductList,
                    })
                );
            });

            return copywritingArr;
        }

        return null;
    }, [financeProducts, isUpdate, t, translate]);
    const extra = useMemo(
        () =>
            isUpdate &&
            !disabled && (
                <Button key="submit" form="availableFinanceProductTypesForm" htmlType="submit" type="primary">
                    {t('bankDetails:actions.update')}
                </Button>
            ),
        [disabled, isUpdate, t]
    );

    return (
        <CollapsibleWrapper defaultActiveKey={t('financeProductDetails:fields.financeProductType.label')}>
            <Panel
                key={t('financeProductDetails:fields.financeProductType.label')}
                extra={extra}
                header={t('financeProductDetails:fields.financeProductType.label')}
                style={{ marginBottom: '20px' }}
            >
                <Row gutter={14}>
                    <Col {...colSpan}>
                        <SwitchField
                            {...t('bankDetails:fields.showMarginOfFinance', { returnObjects: true })}
                            disabled={disabled}
                            name="showMarginOfFinance"
                            {...yesNoSwitch}
                            required
                        />
                    </Col>
                </Row>
                <TransferField
                    dataSource={availableFinanceProductType}
                    disabled={disabled}
                    filterOption={filterOption}
                    listStyle={{ width: '100%' }}
                    name="availableFinanceProductTypes"
                    render={item => item.title}
                    titles={[
                        t('financeProductDetails:subTitles.productTypePool'),
                        t('financeProductDetails:subTitles.selectedProductType'),
                    ]}
                    required
                    showSearch
                />
                {isUpdate && usedFinanceProductTypeCopywriting?.length > 0 && (
                    <div style={{ fontSize: '10px', color: '#9c9c9c', lineHeight: '5px', margin: 0 }}>
                        <p>{t('bankDetails:usedFinanceProductType.note')}</p>
                        {usedFinanceProductTypeCopywriting.map(note => (
                            <p>-{renderMarkdown(note)}</p>
                        ))}
                    </div>
                )}
            </Panel>
        </CollapsibleWrapper>
    );
};

export default FinanceProductTypeForm;
